import { z } from "zod";
import type { FeatureKey } from "./pricing";

/**
 * Uploaded material (spec §5.2 `sources`, §6.2). Each kind maps to the Appendix B capture feature that
 * allows it, so the server checks the plan before issuing an upload URL.
 */
export const SOURCE_KINDS = ["pdf", "slide", "doc", "text", "image", "audio"] as const;
export const sourceKindSchema = z.enum(SOURCE_KINDS);
export type SourceKind = z.infer<typeof sourceKindSchema>;

/** Accepted upload types (spec §5.2). */
export const UPLOAD_MIME_TYPES = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "slide",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "doc",
  "image/png": "image",
  "image/jpeg": "image",
  "image/heic": "image",
  "image/webp": "image",
  // Website audio clips (v1.6 §6.2), transcribed by AI on the server; phone audio never uploads.
  "audio/mpeg": "audio",
  "audio/mp4": "audio",
  "audio/x-m4a": "audio",
  "audio/wav": "audio",
  "audio/aac": "audio",
} as const satisfies Record<string, SourceKind>;
export type UploadMimeType = keyof typeof UPLOAD_MIME_TYPES;
export const uploadMimeTypeSchema = z.enum(Object.keys(UPLOAD_MIME_TYPES) as [UploadMimeType, ...UploadMimeType[]]);

export const FEATURE_FOR_KIND: Record<SourceKind, FeatureKey> = {
  pdf: "capture_pdf_slides",
  slide: "capture_pdf_slides",
  doc: "capture_docx",
  text: "capture_text",
  image: "capture_images",
  // Uploaded audio is a website clip; phone imports use capture_audio_import on /sources/recording.
  audio: "web_audio_upload",
};

export const SOURCE_STATUSES = ["awaiting_upload", "uploaded", "transcribing", "parsed", "failed"] as const;
export const sourceStatusSchema = z.enum(SOURCE_STATUSES);

const filename = z.string().trim().min(1).max(200);

export const signUploadRequestSchema = z.object({
  filename,
  mime_type: uploadMimeTypeSchema,
  size_bytes: z.number().int().positive(),
});
export type SignUploadRequest = z.infer<typeof signUploadRequestSchema>;

export const signUploadResponseSchema = z.object({
  source_id: z.string().uuid(),
  bucket: z.literal("sources"),
  path: z.string().min(1),
  token: z.string().min(1),
});

/** Longest photo text the browser may send; one photographed page is a few thousand characters. */
export const DEVICE_TEXT_MAX_CHARS = 8000;
/** Below this many letters or this recogniser confidence (0–100), the photo goes to AI reading instead (v1.7). */
export const DEVICE_TEXT_MIN_LETTERS = 25;
export const DEVICE_TEXT_MIN_CONFIDENCE = 60;

export const completeUploadRequestSchema = z.object({
  source_id: z.string().uuid(),
  /** Photos only: text recognised in the browser (spec v1.7 §6.2), so a clear photo needs no AI reading. */
  device_text: z.object({ text: z.string().max(DEVICE_TEXT_MAX_CHARS), confidence: z.number().min(0).max(100) }).optional(),
});

/** Whether browser-recognised photo text is good enough to study from without AI reading (v1.7). */
export function usableDeviceText(t: { text: string; confidence: number } | null | undefined): boolean {
  if (!t) return false;
  return (t.text.match(/\p{L}/gu)?.length ?? 0) >= DEVICE_TEXT_MIN_LETTERS && t.confidence >= DEVICE_TEXT_MIN_CONFIDENCE;
}

export const pasteTextRequestSchema = z.object({
  title: z.string().trim().min(1).max(120),
  /** Upper bound is the paste limit from /config/pricing; the server checks the live value. */
  text: z.string().trim().min(200, "Paste at least a few paragraphs so there is enough to study."),
});
export type PasteTextRequest = z.infer<typeof pasteTextRequestSchema>;

export const sourceSchema = z.object({
  id: z.string().uuid(),
  kind: sourceKindSchema,
  filename: z.string(),
  size_bytes: z.number().int().nonnegative(),
  status: sourceStatusSchema,
  unit_count: z.number().int().nonnegative().nullable(),
  set_id: z.string().uuid().nullable(),
  created_at: z.string(),
});
export type Source = z.infer<typeof sourceSchema>;

export const sourceListResponseSchema = z.object({ items: z.array(sourceSchema) });

/**
 * Checks a file's first bytes against its declared type (spec §11.2 type sniffing), so a renamed file can't slip
 * past the allowlist. Office files are zip containers; HEIC is an ISO media file with an image brand.
 */
export function sniffMatches(mime: UploadMimeType, head: Uint8Array): boolean {
  const ascii = (from: number, to: number) => String.fromCharCode(...head.subarray(from, to));
  switch (mime) {
    case "application/pdf":
      return ascii(0, 5) === "%PDF-";
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return head[0] === 0x50 && head[1] === 0x4b && head[2] === 0x03 && head[3] === 0x04;
    case "image/png":
      return head[0] === 0x89 && ascii(1, 4) === "PNG";
    case "image/jpeg":
      return head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff;
    case "image/webp":
      return ascii(0, 4) === "RIFF" && ascii(8, 12) === "WEBP";
    case "image/heic":
      return ascii(4, 8) === "ftyp" && ["heic", "heix", "hevc", "hevx", "mif1", "msf1"].includes(ascii(8, 12));
    case "audio/mpeg":
      // ID3 tag, or an MPEG audio frame sync.
      return ascii(0, 3) === "ID3" || (head[0] === 0xff && (head[1]! & 0xe0) === 0xe0);
    case "audio/mp4":
    case "audio/x-m4a":
      return ascii(4, 8) === "ftyp";
    case "audio/wav":
      return ascii(0, 4) === "RIFF" && ascii(8, 12) === "WAVE";
    case "audio/aac":
      // ADTS frame sync.
      return head[0] === 0xff && (head[1]! & 0xf6) === 0xf0;
  }
}
